import { useState } from 'react'
import { createClient } from '@supabase/supabase-js'
import { track as phTrack } from '../utils/posthog'
import '../styles/WeeklyCheckin.css'

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
)

/**
 * WeeklyCheckin — modal flow for a single protocol's weekly check-in.
 *
 * Asks the user days completed for EACH of the 3 weekly tasks (daily_micro_wins).
 * Computes weekly adherence as average days completed / 7 across the 3 tasks.
 * Maps overall weekly adherence to the adaptive branch:
 *   ≥80% → did_well       (e.g. 6+/7 days on most tasks)
 *   30-79% → partial      (some days on most tasks)
 *   <30% → struggled      (almost nothing happened this week)
 *
 * Stores per-task days in protocol_checkins.metadata (JSONB) so we can
 * surface "you nailed Task 2 but missed Task 1" content later.
 */
export default function WeeklyCheckin({ userProtocol, tasksOverride, onComplete, onCancel }) {
  const [step, setStep] = useState('tasks') // tasks | response | saving | done
  const [taskDays, setTaskDays] = useState([null, null, null])
  const [userNote, setUserNote] = useState('')
  const [error, setError] = useState(null)

  const content = userProtocol.content
  // Prefer the exact tasks the dashboard rendered for the user this week
  // (which may be picked from the personalized 700-tip bank). Falls back to
  // the protocol's static daily_micro_wins. Without this, the check-in
  // shows the static tasks while the dashboard showed personalized ones —
  // a mismatch the user notices immediately.
  const tasks = ((tasksOverride && tasksOverride.length > 0)
    ? tasksOverride
    : (content.daily_micro_wins || [])
  ).slice(0, 3)
  const taskCount = tasks.length || 1

  const allTasksAnswered = taskDays.slice(0, taskCount).every(d => d !== null)

  // Compute weekly adherence: avg days completed / 7 across all tasks.
  // Decided on 7-day target as the simple rule; more nuanced per-task
  // targets land later when each protocol declares its own cadence.
  const totalDays = taskDays.slice(0, taskCount).reduce((s, d) => s + (d || 0), 0)
  const weeklyTarget = taskCount * 7
  const adherencePct = weeklyTarget > 0 ? Math.round((totalDays / weeklyTarget) * 100) : 0

  // Map adherence to the adaptive branch
  const outcome = (() => {
    if (adherencePct >= 80) return 'did_well'
    if (adherencePct >= 30) return 'partial'
    return 'struggled'
  })()

  const handleTaskDays = (taskIdx, days) => {
    const next = [...taskDays]
    next[taskIdx] = days
    setTaskDays(next)
  }

  const branchCopy = () => {
    if (outcome === 'did_well') return content.if_did_well || "Great work this week — keep it going."
    if (outcome === 'partial') return content.if_did_partial || 'Halfway is real progress. Same goal next week.'
    return content.if_did_zero || "No judgment. Let's make it smaller next week."
  }

  const branchTitle = () => {
    if (outcome === 'did_well') return "That's the work."
    if (outcome === 'partial') return 'Halfway counts.'
    return "Let's reset."
  }

  const handleSave = async () => {
    setStep('saving')
    setError(null)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        setError('Session expired. Please sign in again.')
        setStep('response')
        return
      }

      // The protocol_checkins table has a CHECK (days_completed BETWEEN 0 AND 7)
      // constraint from the original 1-task design. Now we sum across 3 tasks
      // (so totalDays can be 0-21). Store the AVERAGE per task instead so the
      // value stays in 0-7 — keeps the constraint, preserves the percentage,
      // and matches the dashboard's days_completed/goal_target math.
      const avgDaysPerTask = Math.round(totalDays / Math.max(1, taskCount))

      const { error: insertError } = await supabase.from('protocol_checkins').insert({
        user_id: user.id,
        user_protocol_id: userProtocol.id,
        week_number: userProtocol.current_week,
        days_completed: avgDaysPerTask,        // 0-7, average across the tasks
        goal_target: 7,                         // baseline target (per-task)
        user_note: userNote || null,
        outcome,
        branch_shown: outcome,
      })
      if (insertError) {
        console.warn('checkin insert failed:', insertError)
        setError('Could not save your check-in. ' + insertError.message)
        setStep('response')
        return
      }

      // Advance week + optionally adjust difficulty
      const updates = {
        current_week: userProtocol.current_week + 1,
      }
      if (outcome === 'struggled' && userProtocol.difficulty_tier !== 'reduced') {
        updates.difficulty_tier = 'reduced'
      } else if (outcome === 'did_well' && userProtocol.difficulty_tier === 'reduced') {
        updates.difficulty_tier = 'standard'
      }

      const { error: updateError } = await supabase
        .from('user_protocols')
        .update(updates)
        .eq('id', userProtocol.id)
      if (updateError) {
        console.warn('user_protocol update failed:', updateError)
      }

      phTrack('weekly_checkin_completed', {
        protocol_key: userProtocol.protocol_key,
        category: userProtocol.category,
        week_number: userProtocol.current_week,
        days_completed_avg: avgDaysPerTask,
        adherence_pct: adherencePct,
        outcome,
        wrote_note: !!userNote?.trim(),
      })

      setStep('done')
      setTimeout(() => onComplete(), 1400)
    } catch (err) {
      setError('Unexpected error: ' + (err.message || String(err)))
      setStep('response')
    }
  }

  return (
    <div className="weekly-checkin-overlay" onClick={onCancel}>
      <div className="weekly-checkin-modal" onClick={(e) => e.stopPropagation()}>
        {step === 'tasks' && (
          <>
            <div className="checkin-header">
              <h2>Week {userProtocol.current_week} Check-in</h2>
              <button className="close-btn" onClick={onCancel} aria-label="Close">×</button>
            </div>

            <p className="checkin-intro">
              For each of this week's tasks, tell me how many days you actually did it. Be honest — partial wins are still wins.
            </p>

            <div className="task-checkin-list">
              {tasks.map((task, idx) => (
                <div className="task-checkin-row" key={idx}>
                  <div className="task-text">
                    <span className="task-num">{idx + 1}.</span> {task}
                  </div>
                  <div className="day-picker compact">
                    {[0, 1, 2, 3, 4, 5, 6, 7].map((n) => (
                      <button
                        key={n}
                        type="button"
                        className={`day-btn ${taskDays[idx] === n ? 'selected' : ''} ${n === 0 ? 'zero' : n >= 5 ? 'win' : 'partial'}`}
                        onClick={() => handleTaskDays(idx, n)}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <p className="day-legend">days completed for each task (0 = none, 7 = every day)</p>

            {allTasksAnswered && (
              <div className="weekly-summary">
                <div className="summary-bar-track">
                  <div className="summary-bar-fill" style={{ width: `${adherencePct}%` }} />
                </div>
                <p className="summary-text">
                  Weekly adherence: <strong>{adherencePct}%</strong> ({totalDays} days across {taskCount} tasks)
                </p>
              </div>
            )}

            <div className="checkin-footer">
              <button type="button" className="back-btn" onClick={onCancel}>Cancel</button>
              <button
                type="button"
                className="save-btn"
                onClick={() => setStep('response')}
                disabled={!allTasksAnswered}
              >
                See Coach's response →
              </button>
            </div>
          </>
        )}

        {step === 'response' && (
          <>
            <div className="checkin-header">
              <h2>{branchTitle()}</h2>
              <button className="close-btn" onClick={onCancel} aria-label="Close">×</button>
            </div>

            <div className="weekly-summary inline">
              <div className="summary-bar-track">
                <div className="summary-bar-fill" style={{ width: `${adherencePct}%` }} />
              </div>
              <p className="summary-text">
                <strong>{adherencePct}%</strong> · {totalDays} of {weeklyTarget} possible days
              </p>
            </div>

            <p className="branch-copy">{branchCopy()}</p>

            {(() => {
              // Preview next week's optional stretch goal if the protocol
              // defines one. The 3 core tasks remain mandatory; this is bonus.
              const additions = content.weekly_additions || []
              const nextAddition = additions[userProtocol.current_week]
              if (!nextAddition) return null
              return (
                <div className="next-week-preview">
                  <h5>Bonus stretch goal next week (optional):</h5>
                  <p>{nextAddition}</p>
                  <p className="next-week-note">
                    Your 3 core habits stay the same. This is extra — try it if you've nailed the basics.
                  </p>
                </div>
              )
            })()}

            <div className="user-note">
              <label htmlFor="checkin-note">Want to add a note? (optional)</label>
              <textarea
                id="checkin-note"
                placeholder="What got in the way? What worked? Just for you and me."
                value={userNote}
                onChange={(e) => setUserNote(e.target.value)}
                rows={3}
              />
            </div>

            {error && <div className="checkin-error">⚠️ {error}</div>}

            <div className="checkin-footer">
              <button type="button" className="back-btn" onClick={() => setStep('tasks')}>← Change my answers</button>
              <button type="button" className="save-btn" onClick={handleSave}>Save check-in</button>
            </div>
          </>
        )}

        {step === 'saving' && (
          <div className="checkin-saving">
            <p>Saving your check-in…</p>
          </div>
        )}

        {step === 'done' && (
          <div className="checkin-done">
            <h2>Got it.</h2>
            <p>See you next week.</p>
            <p className="signoff">— Coach K</p>
          </div>
        )}
      </div>
    </div>
  )
}
